import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';

import MarkdownRenderer from '../components/MarkdownRenderer';
import TagPreview from '../components/TagPreview';
import { buildApiUrl } from '../utils/api';

import '../static/styles/ProjectViewer.css';

export default function ProjectViewer() {
    const { repo: repoParam } = useParams();
    const repo = decodeURIComponent(repoParam ?? '');
    const [project, setProject] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const handleClose = () => {
        if (window.opener) {
            window.close();
            return;
        }

        if (window.history.length > 1) {
            window.history.back();
            return;
        }

        window.location.href = '/projects';
    };

    useEffect(() => {
        if (!repo) {
            setError('Repository not specified.');
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        const endpoint = buildApiUrl(`/portfolio/${encodeURIComponent(repo)}`);

        fetch(endpoint, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Unable to load repository details (${response.status}).`);
                }

                return response.json();
            })
            .then((payload) => {
                setProject(payload);
                setLoading(false);
            })
            .catch((err) => {
                if (err.name === 'AbortError') {
                    return;
                }
                setError(err.message || 'Something went wrong while loading the project.');
                setLoading(false);
            });

        return () => controller.abort();
    }, [repo]);

    if (loading) {
        return (
            <div className="project-viewer__state">
                <ProgressSpinner style={{ width: '4rem', height: '4rem' }} strokeWidth="6" />
                <p className="mt-3">Loading project…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="project-viewer__state">
                <p className="project-viewer__error">{error}</p>
                <Button label="Back" icon="pi pi-arrow-left" outlined onClick={handleClose} />
            </div>
        );
    }

    const { details, readme } = project ?? {};
    const repoName = details?.name ?? repo;
    const topics = details?.topics ?? [];
    const stars = details?.stargazers_count ?? 0;
    const githubUrl = details?.html_url ?? details?.url ?? null;

    return (
        <div className="project-viewer">
            <div>
                <Button
                    label="Back to Projects"
                    icon="pi pi-arrow-left"
                    className="project-viewer__back"
                    outlined
                    rounded
                    onClick={handleClose}
                />
            </div>

            <header className="project-viewer__header">
                <div className="project-viewer__title">
                    <h1 className="mt-0">{repoName}</h1>
                    {details?.description && <p className="gray">{details.description}</p>}
                </div>
            </header>
            <main className="project-viewer__content">
                <div className="project-viewer__meta">
                    <div className="project-viewer__meta-left">
                        <TagPreview tags={topics} full />
                        <Divider />
                        {githubUrl && (
                        <Button
                            label="View on GitHub"
                            icon="pi pi-github"
                            className="project-viewer__github"
                            outlined
                            rounded
                            onClick={() => window.open(githubUrl, '_blank', 'noopener')}
                        />
                    )}
                        <span className="project-viewer__stars">
                            <i className="pi pi-star-fill" /> {stars.toLocaleString()} stars
                        </span>
                    </div>
                </div>
                <MarkdownRenderer content={readme} repo={repoName} />
            </main>
        </div>
    );
}
